
/**
 * Converte uma string de data no formato YYYY-MM-DD para um objeto Date local
 * sem problemas de timezone
 */
export function parseLocalDate(dateString: string): Date {
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day); // month é 0-indexed no JavaScript
}

/**
 * Converte um objeto Date para string no formato YYYY-MM-DD
 * usando a data local (sem conversão de timezone)
 */
export function formatLocalDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Normaliza uma data para o formato YYYY-MM-DD independente do formato de entrada
 * Aceita: ISO strings, date strings, ou Date objects
 */
export function normalizeDateForComparison(dateInput: string | Date): string {
  if (!dateInput) return '';
  
  let date: Date;
  
  if (typeof dateInput === 'string') {
    // Se já está no formato YYYY-MM-DD, retorna como está
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateInput)) {
      return dateInput;
    }
    
    // Se é um ISO string completo, converte para Date
    if (dateInput.includes('T') || dateInput.includes(':')) {
      date = new Date(dateInput);
    } else {
      // Tenta fazer parse da string
      date = new Date(dateInput);
    }
  } else {
    date = dateInput;
  }
  
  // Se a data é inválida, retorna string vazia
  if (isNaN(date.getTime())) {
    return '';
  }
  
  // Retorna no formato YYYY-MM-DD
  return formatLocalDate(date);
}

/**
 * Compara duas datas normalizadas
 * Retorna: -1 se date1 < date2, 0 se iguais, 1 se date1 > date2
 */
export function compareDates(date1: string | Date, date2: string | Date): number {
  const normalizedDate1 = normalizeDateForComparison(date1);
  const normalizedDate2 = normalizeDateForComparison(date2);
  
  if (normalizedDate1 < normalizedDate2) return -1;
  if (normalizedDate1 > normalizedDate2) return 1;
  return 0;
}

/**
 * Verifica se uma data está dentro de um período (inclusivo)
 * Útil para validar se created_at está entre start_date e end_date
 */
export function isDateInPeriod(
  targetDate: string | Date, 
  startDate: string | Date, 
  endDate: string | Date
): boolean {
  const normalizedTarget = normalizeDateForComparison(targetDate);
  const normalizedStart = normalizeDateForComparison(startDate);
  const normalizedEnd = normalizeDateForComparison(endDate);
  
  return normalizedTarget >= normalizedStart && normalizedTarget <= normalizedEnd;
}

/**
 * Converte uma data para o formato que o N8N/webhook espera
 * Sempre retorna YYYY-MM-DD para consistência
 */
export function formatDateForWebhook(date: string | Date): string {
  return normalizeDateForComparison(date);
}

/**
 * Helper para debug - mostra os formatos de data sendo comparados
 */
export function debugDateComparison(date1: string | Date, date2: string | Date): {
  original1: string | Date;
  original2: string | Date;
  normalized1: string;
  normalized2: string;
  comparison: number;
} {
  const normalized1 = normalizeDateForComparison(date1);
  const normalized2 = normalizeDateForComparison(date2);
  
  return {
    original1: date1,
    original2: date2,
    normalized1,
    normalized2,
    comparison: compareDates(date1, date2)
  };
}
